import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireRemoteConfig } from '@angular/fire/remote-config';

import { AnalyticsEventCategory } from '../../../shared/configs/analytics-event-category.config';
import { AnalyticsEventTypes } from '../../../shared/configs/analytics-event-types.config';
import { UserDetailsService } from '../user-details/user-details.service';

@Injectable({
  providedIn: 'root',
})
export class FirestoreAnalyticsService {
  private analyticsEnabled: boolean = false;
  constructor(
    private userDetailsService: UserDetailsService,
    private firestore: AngularFirestore,
    private remoteConfig: AngularFireRemoteConfig,
  ) {
    this.initializeRemoteConfig();
  }

  private async initializeRemoteConfig(): Promise<void> {
    try {
      await this.remoteConfig.fetchAndActivate();
      this.analyticsEnabled = await this.remoteConfig.getBoolean('firestore_events_enabled');
    } catch (err) {
      // If fetching fails, analyticsEnabled remains false
    }
  }

  private getCurrentDateTime(): string {
    const now: Date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    };

    return new Intl.DateTimeFormat('en-GB', options).format(now);
  }

  /**
   * Logs an event to Firestore.
   * @param eventType Type of the event (e.g., click, submit)
   * @param eventCategory Category of the event (e.g., button click, form submission)
   * @param eventLabel Label associated with the event
   * @param sessionId id for current unique user session
   */
  sendEvent(
    eventType: AnalyticsEventTypes,
    eventCategory: AnalyticsEventCategory,
    eventLabel: string,
    sessionId: string,
  ): void {
    if (!this.analyticsEnabled) return;
    const user = this.userDetailsService.userDetails;
    const time = this.getCurrentDateTime();
    // Log event to Firestore
    this.firestore.collection('admin-web-events').add({
      event_type: eventType,
      event_category: eventCategory,
      event_label: eventLabel,
      user_id: user?.id || '',
      session_id: sessionId,
      timestamp: time,
    });
  }

  sendPageViewEvent(
    pageName: string,
    timeSpent: number,
    bounce: boolean,
    params: Map<string, any>,
    sessionId: string,
  ): void {
    if (!this.analyticsEnabled) return;

    const user = this.userDetailsService.userDetails;
    const time = this.getCurrentDateTime();
    const paramsObject = this.mapToObject(params);

    this.firestore.collection('admin-web-events').add({
      event_name: 'page_view',
      page_name: pageName,
      bounce,
      time_spent: timeSpent,
      user_id: user?.id || '',
      session_id: sessionId,
      timestamp: time,
      ...paramsObject,
    });
  }

  sendInteractionEvent(eventName: string, params: Map<string, any>, sessionId: string): void {
    if (!this.analyticsEnabled) return;

    const user = this.userDetailsService.userDetails;
    const time = this.getCurrentDateTime();
    const paramsObject = this.mapToObject(params);

    this.firestore.collection('admin-web-events').add({
      event_name: eventName,
      user_id: user?.id || '',
      session_id: sessionId,
      timestamp: time,
      ...paramsObject,
    });
  }

  private mapToObject(map: Map<string, any>): { [key: string]: any } {
    const obj: { [key: string]: any } = {};
    map.forEach((value, key) => {
      obj[key] = value;
    });

    return obj;
  }

  /**
   * Configures analytics for tracking page views and other global data.
   * @param pagePath The path of the page being viewed
   * @param sessionId id for current unique user session
   */
  setAnalyticsConfig(pagePath: string, sessionId: string): void {
    if (!this.analyticsEnabled) return;
    const user = this.userDetailsService.userDetails;
    const time = this.getCurrentDateTime();
    // Log page view to Firestore
    this.firestore.collection('admin-web-events').add({
      event_name: 'page_view',
      page_path: pagePath,
      user_id: user?.id || '',
      timestamp: time,
      session_id: sessionId,
    });
  }
}
